import { useRef, useState } from "react";
import {
  documentsymbolgreen,
  greyuploadicon,
  uploadbigicon,
} from "../../landingscreen/patientcharts/tabs/Document/svgs";
import { Backdrop, CircularProgress } from "@mui/material";
import ToastMessage from "../../utils/mui/righttoptoast";
import { practiceFaxProfile } from "../../config/practice";
import { faxSendErrorMessage, faxSendOk, newFaxSendKey, sendFax } from "../faxApi";

/* ------------------ CONSTANTS ------------------ */
const ALLOWED_TYPES = ["application/pdf", "image/png", "image/jpeg"];
const MAX_FILE_SIZE_MB = 25;
const MAX_COVER_TEXT = 1024; // RingCentral coverPageText limit

const DEFAULT_HIPAA_COVER_TEXT =
  "CONFIDENTIALITY NOTICE: This fax transmission, including any attachments, " +
  "contains information that may be confidential and protected health information " +
  "(PHI) under federal law (HIPAA). It is intended solely for the use of the " +
  "individual or entity to whom it is addressed. If you are not the intended " +
  "recipient, you are hereby notified that any review, disclosure, copying, " +
  "distribution, or use of the contents of this transmission is strictly " +
  "prohibited. If you have received this fax in error, please notify the sender " +
  "immediately by telephone and destroy all copies of the original message.";

/* Normalize a US number to E.164 (+1XXXXXXXXXX). Returns null if invalid. */
const toE164 = (value) => {
  const trimmed = (value || "").trim();
  if (trimmed.startsWith("+")) {
    const digits = trimmed.slice(1).replace(/\D/g, "");
    return digits.length >= 10 ? `+${digits}` : null;
  }
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
};

/* ------------------ COMPONENT ------------------ */
const SendFaxModal = ({ open, onClose }) => {
  const [file, setFile] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [coverText, setCoverText] = useState(DEFAULT_HIPAA_COVER_TEXT);
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [openToast, setOpenToast] = useState(false);
  // The id of the send being made (faxApi.newFaxSendKey). Kept while the
  // same submit is retried after no answer (the server then answers the
  // first send instead of sending twice); dropped when the fax changes, after
  // a success and after any answer from the server.
  const sendKeyRef = useRef(null);

  if (!open) return null;

  // The practice's fax line (config/practice.js FAX_PROFILES).
  const faxProfile = practiceFaxProfile() || {};

  const handleToastClose = () => setOpenToast(false);

  const resetForm = () => {
    setFile(null);
    setRecipient("");
    setCoverText(DEFAULT_HIPAA_COVER_TEXT);
    setToastMessage("");
    setOpenToast(false);
    sendKeyRef.current = null;
  };

  /* ------------------ FILE HANDLER ------------------ */
  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    e.target.value = "";
    if (!selected) return;
    sendKeyRef.current = null;

    if (!ALLOWED_TYPES.includes(selected.type)) {
      setToastMessage("Only PDF, PNG, JPG files are allowed");
      setToastSeverity("error");
      setOpenToast(true);
      return;
    }
    if (selected.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setToastMessage(`File must be under ${MAX_FILE_SIZE_MB}MB`);
      setToastSeverity("error");
      setOpenToast(true);
      return;
    }
    setFile(selected);
  };

  const normalizedRecipient = toE164(recipient);
  const isSendDisabled = !file || !normalizedRecipient || loading;

  /* ------------------ SUBMIT ------------------ */
  const submit = async () => {
    try {
      setLoading(true);
      if (!sendKeyRef.current) sendKeyRef.current = newFaxSendKey();

      const formData = new FormData();
      formData.append("to", normalizedRecipient);
      formData.append("attachment", file);
      formData.append("cover_page_text", coverText.slice(0, MAX_COVER_TEXT));

      // The practice's own route (faxApi.sendFax): 7825 v2 POST
      // /v1/7825/fax/send, pinned; 9928 v1 POST /v1/fax/send as before. Both
      // multipart.
      const res = await sendFax(formData, { idempotencyKey: sendKeyRef.current });

      // The api client resolves to the response BODY ({result, details, data}),
      // not the axios response, so res.status is undefined: every successful
      // send was reported "Failed" and staff resent the same fax (7 copies to
      // one number, 2026-09-29). Accept either shape.
      if (faxSendOk(res)) {
        sendKeyRef.current = null;
        setToastMessage("Fax submitted successfully");
        setToastSeverity("success");
        setOpenToast(true);
        setTimeout(() => {
          setLoading(false);
          resetForm();
          onClose();
        }, 1200);
      } else {
        throw new Error(res?.message || "Failed to send fax");
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
      // The server answered (refused or failed): the next try is a new send.
      // No answer at all: keep the id, so a retry can't send it twice.
      if (error?.response) sendKeyRef.current = null;
      const detail = faxSendErrorMessage(error);
      setToastMessage(detail && detail !== "Failed to send fax" ? `Failed to send fax: ${detail}` : "Failed to send fax");
      setToastSeverity("error");
      setOpenToast(true);
    }
  };

  /* ------------------ UI ------------------ */
  return (
    <>
      <Backdrop sx={{ color: "#fff", zIndex: 9999 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40">
        <div className="flex min-h-screen items-start justify-center py-10">
          <div className="w-full max-w-md rounded-xl bg-white shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div className="text-lg font-semibold flex items-center gap-2">
                <span>{greyuploadicon}</span>
                Send Fax
              </div>
              <button
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="px-5 py-4">
              {/* The number recipients see (the practice's RingCentral line) */}
              {faxProfile.sendFrom && (
                <p className="mb-3 text-xs text-[#7E7E7E] text-start" data-testid="fax-sends-from">
                  Sends from {faxProfile.sendFrom}
                </p>
              )}
              {/* Recipient */}
              <p className="mb-2 text-sm font-semibold text-start">
                Recipient Fax Number
              </p>
              <input
                type="tel"
                placeholder={faxProfile.placeholder || "e.g. (432) 555-0123 or +14325550123"}
                value={recipient}
                onChange={(e) => {
                  sendKeyRef.current = null;
                  setRecipient(e.target.value);
                }}
                className={`w-full mb-1 rounded-full px-4 py-2 text-sm border bg-[#F5F6FA]
                  ${
                    recipient && !normalizedRecipient
                      ? "border-red-400"
                      : "border-[#D5D5D5]"
                  }`}
              />
              {recipient && !normalizedRecipient && (
                <p className="mb-2 text-xs text-red-500 text-start">
                  Enter a valid 10-digit US fax number
                </p>
              )}

              {/* Upload */}
              <label
                className="mt-3 flex flex-col items-center justify-center rounded-lg border-[2px]
                  border-[#E5E7EB] p-6 text-center cursor-pointer"
              >
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="hidden"
                  onChange={handleFileChange}
                />

                {!file ? (
                  <>
                    <div className="mb-2 bg-[#F3F4F6] rounded-full h-12 w-12 flex items-center justify-center">
                      {uploadbigicon}
                    </div>
                    <p className="text-sm font-medium">
                      Click to upload document
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF, PNG, JPG up to {MAX_FILE_SIZE_MB}MB
                    </p>
                  </>
                ) : (
                  <>
                    <div className="mb-2 bg-[#D0FAE5] rounded-full h-10 w-10 flex items-center justify-center">
                      {documentsymbolgreen}
                    </div>
                    <div className="mt-1 w-full flex items-center justify-between rounded-md border p-2 text-left">
                      <div className="truncate">
                        <p className="text-sm font-medium truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setFile(null);
                        }}
                        className="ml-2 text-xs text-red-500 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </>
                )}
              </label>

              {/* Cover page text */}
              <p className="mt-4 mb-2 text-sm font-semibold text-start">
                Cover Page Note
              </p>
              <textarea
                rows={6}
                maxLength={MAX_COVER_TEXT}
                value={coverText}
                onChange={(e) => {
                  sendKeyRef.current = null;
                  setCoverText(e.target.value);
                }}
                className="w-full rounded-xl border border-[#D5D5D5] bg-[#F5F6FA] px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 text-end">
                {coverText.length}/{MAX_COVER_TEXT}
              </p>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t px-5 py-3">
              <button
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                className="rounded-md border px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                disabled={isSendDisabled}
                onClick={submit}
                className={`rounded-md px-4 py-2 text-sm text-white ${
                  isSendDisabled
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Send Fax
              </button>
            </div>
          </div>
        </div>

        <ToastMessage
          message={toastMessage}
          severity={toastSeverity}
          open={openToast}
          handleClose={handleToastClose}
        />
      </div>
    </>
  );
};

export default SendFaxModal;
